import React, { useEffect, useState, useRef, useMemo } from "react";
import axios from "axios";
import { Button } from "@chakra-ui/react";
import AutoComplete from "react-select";
import FeeTemplateSectionReadOnly from "./FeeTemplateSectionReadOnly";
import { getFeeTypeDisplayName } from "../../feeGroups/utils/feeTypeDisplayNames";
import { ImSpinner8 } from "react-icons/im";
import { TokenManager } from "utils/tokenManager";

type FeeTemplate = {
  feetemplate_id: string;
  asset: string;
  asset_type: string;
  type: string;
  fixed: string;
  percentage: string;
  min_amount: string;
  max_amount: string;
  is_active: boolean;
  decimal: number;
  network_display?: string;
};

type FeeGroupDetail = {
  feegroup_id: string;
  name: string;
  description: string;
  is_default: boolean;
  is_active: boolean;
  created_date: string;
};

const formatDecimal = (value: string, decimals: number): string => {
  if (!value || value === "") return "";
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num.toFixed(decimals);
};

const AccountFeeGroupTab: React.FC<{
  detail: any;
  setSwalProps: any;
  fetchAccountDetail: () => Promise<void>;
  isImpersonating?: boolean;
}> = ({ detail, setSwalProps, fetchAccountDetail, isImpersonating }) => {
  const [selectedFeegroupId, setSelectedFeegroupId] = useState<string>("");
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const [isFeegroupLoading, setIsFeegroupLoading] = useState<boolean>(true);
  const feegroupAutoCompleteRef = useRef(null);
  const [feegroupAutoCompleteOptions, setFeegroupAutoCompleteOptions] =
    useState<any>(null);
  const [feegroupList, setFeegroupList] = useState<any>(null);

  // View Detail states
  const [showDetail, setShowDetail] = useState<boolean>(false);
  const [feeGroupDetail, setFeeGroupDetail] = useState<FeeGroupDetail | null>(
    null
  );
  const [feeTemplates, setFeeTemplates] = useState<FeeTemplate[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState<boolean>(false);
  const [openSections, setOpenSections] = useState<{ [key: string]: boolean }>(
    {}
  );

  const userRole = localStorage.getItem("userRole")?.toLowerCase();
  const username = localStorage.getItem("username");

  useEffect(() => {
    const fetchData = async () => {
      if (detail?.feegroup_id) {
        setSelectedFeegroupId(detail.feegroup_id);
      }
      await fetchFeegroups();
      setIsFeegroupLoading(false);
    };

    fetchData();
  }, [detail]);

  useEffect(() => {
    if (feegroupList !== null) {
      setIsFeegroupLoading(true);
      let rebuildListArray: any = [];
      if (feegroupList.length > 0) {
        feegroupList.forEach((i: any) => {
          let newObj = {
            value: i.id,
            label: i.name,
          };
          rebuildListArray.push(newObj);
        });
      }

      setFeegroupAutoCompleteOptions(rebuildListArray);
      setIsFeegroupLoading(false);
    }
  }, [feegroupList]);

  // Reset detail view when fee group changes
  useEffect(() => {
    setShowDetail(false);
    setFeeGroupDetail(null);
    setFeeTemplates([]);
  }, [selectedFeegroupId]);

  const fetchFeegroups = async () => {
    try {
      let requestUrl = `/api/feegroup/all-feegroups`;

      const response = await axios.get(
        process.env.REACT_APP_API_URL + requestUrl
      );

      const feegroupListData = response.data.feegroups?.payload;

      let rebuildListArray: any = [];
      if (feegroupListData.length > 0) {
        feegroupListData.forEach((i: any) => {
          let newObj = {
            id: i.feegroup_id,
            name: i.description,
          };
          rebuildListArray.push(newObj);
        });
      }
      setFeegroupList(rebuildListArray);
    } catch (error) {
      console.error("Error fetching feegroups", error);
    }
  };

  const fetchFeeGroupDetail = async () => {
    if (!selectedFeegroupId) return;

    setIsLoadingDetail(true);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_URL +
          `/api/feegroup/feegroup-detail?feegroup_id=${selectedFeegroupId}&type=subaccount`
      );

      const data = response.data?.feegroup?.payload;
      if (data) {
        setFeeGroupDetail(data.feegroup);

        // Format templates with proper decimal places
        const formattedTemplates = (data.feetemplates || []).map(
          (t: FeeTemplate) => ({
            ...t,
            fixed: formatDecimal(t.fixed, t.decimal),
            percentage: formatDecimal(t.percentage, 2),
            min_amount: formatDecimal(t.min_amount, t.decimal),
            max_amount: formatDecimal(t.max_amount, t.decimal),
          })
        );
        setFeeTemplates(formattedTemplates);

        // Initialize openSections with first type open
        const types: string[] = [
          ...new Set((data.feetemplates || []).map((t: FeeTemplate) => t.type)),
        ].sort() as string[];
        const initialOpenState: { [key: string]: boolean } = {};
        types.forEach((type, index) => {
          initialOpenState[type] = index === 0;
        });
        setOpenSections(initialOpenState);
        setShowDetail(true);
      }
    } catch (error: any) {
      console.error("Error fetching fee group detail:", error);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Failed to load fee group details",
        showConfirmButton: true,
        didClose: () => setSwalProps({}),
      });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleFeegroupAutoCompleteChange = (item: any) => {
    if (item !== null) {
      setSelectedFeegroupId(item.value);
    } else {
      setSelectedFeegroupId("");
    }
  };

  const noFeegroupOptionsMessageRenderer = ({
    inputValue,
  }: {
    inputValue: string;
  }) => {
    return (
      <div className="text-sm">Fee Group not found for "{inputValue}"</div>
    );
  };

  const handleViewDetail = () => {
    if (showDetail) {
      setShowDetail(false);
    } else {
      fetchFeeGroupDetail();
    }
  };

  const handleSubmit = async () => {
    if (!selectedFeegroupId) return;

    const dataToUpdate = {
      id: detail.id,
      feegroup_id: selectedFeegroupId,
      userRole: userRole,
      username: username,
    };

    try {
      setIsUpdating(true);
      await axios
        .post(
          process.env.REACT_APP_API_URL +
            `/api/${userRole}/subaccount/action/update-subaccount`,
          dataToUpdate
        )
        .then(function (response) {
          if (response && response.data?.status === "ok") {
            setSwalProps({
              show: true,
              icon: "success",
              title: "Success",
              html: "Fee group has been successfully updated",
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsUpdating(false);
                fetchAccountDetail();
              },
            });
          } else {
            setIsUpdating(false);
            setSwalProps({
              show: true,
              icon: "error",
              title: "Oops!",
              html: response.data.message,
              showConfirmButton: true,
              didClose: () => {
                setSwalProps({});
                setIsUpdating(false);
              },
            });
          }
        })
        .catch(function (error) {
          setIsUpdating(false);
          setSwalProps({
            show: true,
            icon: "error",
            title: "Oops!",
            html:
              error.response?.data?.message ||
              "Something went wrong, please try again later.",
            showConfirmButton: true,
            didClose: () => {
              setSwalProps({});
              setIsUpdating(false);
            },
          });
        });
    } catch (error) {
      setIsUpdating(false);
      setSwalProps({
        show: true,
        icon: "error",
        title: "Oops!",
        html: "Something went wrong, please try again later.",
        showConfirmButton: true,
        didClose: () => {
          setSwalProps({});
          setIsUpdating(false);
        },
      });
    }
  };

  const groupedTemplates = useMemo(() => {
    const groups: { [key: string]: FeeTemplate[] } = {};
    feeTemplates.forEach((template) => {
      if (!groups[template.type]) {
        groups[template.type] = [];
      }
      groups[template.type].push(template);
    });
    return groups;
  }, [feeTemplates]);

  const areAllOpen =
    Object.keys(openSections).length > 0 &&
    Object.values(openSections).every(Boolean);

  const toggleAllSections = () => {
    const newState = !areAllOpen;
    setOpenSections(
      Object.keys(openSections).reduce(
        (acc, key) => ({ ...acc, [key]: newState }),
        {}
      )
    );
  };

  const isFormValid = selectedFeegroupId !== "";
  const hasChanged = selectedFeegroupId !== detail?.feegroup_id;

  return (
    <div className="px-4 py-8">
      {detail !== null ? (
        <>
          {/* Fee Group Selection Section */}
          <div className="space-y-4 pb-8">
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 text-sm text-navy-700 dark:text-white md:grid-cols-2">
              <div>
                <label className="text-sm font-medium text-gray-800">
                  Fee Group <span className="text-red-500">*</span>
                </label>
                <div className="flex items-start gap-3">
                  <div className="flex-1">
                    <AutoComplete
                      ref={feegroupAutoCompleteRef}
                      isDisabled={
                        isFeegroupLoading ||
                        isUpdating ||
                        feegroupAutoCompleteOptions === null ||
                        feegroupAutoCompleteOptions.length === 0
                      }
                      placeholder={
                        feegroupAutoCompleteOptions === null
                          ? "Retrieving..."
                          : "Please Select"
                      }
                      options={feegroupAutoCompleteOptions}
                      classNamePrefix="custom-autocomplete"
                      className="autocomplete-override"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
                      }}
                      onChange={(item: any) =>
                        handleFeegroupAutoCompleteChange(item)
                      }
                      value={
                        feegroupAutoCompleteOptions
                          ? feegroupAutoCompleteOptions.find(
                              (option: any) =>
                                option.value === selectedFeegroupId
                            )
                          : null
                      }
                      noOptionsMessage={noFeegroupOptionsMessageRenderer}
                      isClearable
                    />
                  </div>
                  <Button
                    onClick={handleViewDetail}
                    isDisabled={
                      !selectedFeegroupId ||
                      isLoadingDetail ||
                      isFeegroupLoading ||
                      isUpdating ||
                      feegroupAutoCompleteOptions === null ||
                      feegroupAutoCompleteOptions.length === 0
                    }
                    isLoading={isLoadingDetail}
                    loadingText="Loading..."
                    className="btn btn-primary whitespace-nowrap text-white"
                    style={{ marginTop: "2px" }}
                  >
                    {showDetail ? "Hide Detail" : "View Detail"}
                  </Button>
                  {!showDetail && (
                    <Button
                      onClick={() => handleSubmit()}
                      isDisabled={!isFormValid || isUpdating || !hasChanged || isImpersonating || TokenManager.isImpersonating()}
                      isLoading={isUpdating}
                      loadingText="Updating..."
                      className="btn btn-primary whitespace-nowrap text-white"
                      style={{ marginTop: "2px" }}
                    >
                      Update Fee Group
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Fee Group Detail Section (Read Only) */}
          {showDetail && feeGroupDetail && (
            <div className="border-t border-gray-200 pt-6">
              {Object.keys(groupedTemplates).length > 0 ? (
                <>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-navy-700">
                      Fee Templates
                    </h3>
                    <button
                      type="button"
                      onClick={toggleAllSections}
                      className="text-sm font-medium text-navy-700"
                    >
                      {areAllOpen ? "Collapse All" : "Show All"}
                    </button>
                  </div>
                  {Object.keys(groupedTemplates)
                    .sort()
                    .map((type) => (
                      <FeeTemplateSectionReadOnly
                        key={type}
                        title={getFeeTypeDisplayName(type)}
                        templates={groupedTemplates[type]}
                        isOpen={openSections[type] ?? false}
                        onToggle={() =>
                          setOpenSections((prev) => ({
                            ...prev,
                            [type]: !prev[type],
                          }))
                        }
                      />
                    ))}
                </>
              ) : (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  No record found
                </div>
              )}
            </div>
          )}

          {showDetail && (
            <div className="flex flex-wrap justify-end pt-4">
              <Button
                onClick={() => handleSubmit()}
                isDisabled={!isFormValid || isUpdating || !hasChanged}
                isLoading={isUpdating}
                loadingText="Updating Fee Group..."
                className="btn btn-primary whitespace-nowrap text-white"
              >
                Update Fee Group
              </Button>
            </div>
          )}

          {/* Loading Detail Skeleton */}
          {isLoadingDetail && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-center py-8">
                <ImSpinner8
                  className="spinner text-primary-500"
                  style={{ fontSize: "1.8rem" }}
                />
              </div>
            </div>
          )}

        </>
      ) : (
        <>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
          <div
            className="mb-3 h-5 animate-pulse rounded-md bg-gray-200"
            style={{ animationDuration: "0.6s" }}
          ></div>
        </>
      )}
    </div>
  );
};

export default AccountFeeGroupTab;
